import { useState, type FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/da0koce859p3u981e3u0';

interface FormState {
  status: 'idle' | 'success' | 'error';
  message: string;
}

function ContactForm() {
  const [state, setState] = useState<FormState>({ status: 'idle', message: '' });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // honeypot 反垃圾：若被填了内容，则静默当作成功返回
    const honeypot = (formData.get('website_alt') as string) || '';
    if (honeypot.trim() !== '') {
      setState({ status: 'success', message: '感谢您的留言，我们会尽快与您联系！' });
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    setState({ status: 'idle', message: '' });
    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setState({ status: 'success', message: '感谢您的留言，我们会尽快与您联系！' });
        form.reset();
      } else {
        setState({
          status: 'error',
          message: isSpam ? '提交过于频繁，请稍后再试。' : (typeof serverMsg === 'string' && serverMsg ? serverMsg : '提交失败，请稍后再试。'),
        });
      }
    } catch {
      setState({ status: 'error', message: '网络异常，请稍后再试。' });
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form="联系我们表单" className="space-y-4" noValidate>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block text-sm font-bold text-foreground-800">您的称呼</label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入您的称呼"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block text-sm font-bold text-foreground-800">电子邮箱</label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="请输入您的邮箱"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>
      <div>
        <label htmlFor="contact-subject" className="mb-1.5 block text-sm font-bold text-foreground-800">主题</label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          required
          placeholder="例如：漫画合作 / 版权咨询"
          className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="mb-1.5 block text-sm font-bold text-foreground-800">留言内容</label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请简要描述您的需求（最多 500 字）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
      </div>
      {/* 反垃圾 honeypot 字段 */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />
      {state.message && (
        <p
          className={`flex items-start gap-2 rounded-md px-4 py-3 text-sm ${
            state.status === 'success' ? 'bg-secondary-100 text-secondary-900' : 'bg-primary-100 text-primary-800'
          }`}
          role={state.status === 'error' ? 'alert' : 'status'}
        >
          <i className={`mt-0.5 ${state.status === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'}`}></i>
          {state.message}
        </p>
      )}
      <button
        type="submit"
        className="inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-8 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
      >
        <i className="ri-send-plane-fill mr-2"></i>提交留言
      </button>
    </form>
  );
}

export default ContactForm;